import { useRef, useState } from "react";
import { toPng } from "html-to-image";
import { Share2, Download, Loader2, MessageSquare, Flame } from "lucide-react";
import { toast } from "sonner";
import { Events } from "@/lib/analytics";

const SHARE_URL = "https://sayitbetter.me";
const SHARE_CAPTION =
  "I just rewrote a risky text using SayItBetter — an AI coach that stops you from sending something you'll regret. Try it free: " +
  SHARE_URL;

const LEVEL = {
  green:  { label: "Calm",     color: "#4FB3A5", bg: "#EAF6F4", icon: "🟢" },
  yellow: { label: "Tense",    color: "#E9A13B", bg: "#FFF3DC", icon: "🟡" },
  red:    { label: "High-risk",color: "#E07A7A", bg: "#FEE4E4", icon: "🔴" },
};

function levelFromScore(score) {
  if (score < 3.5) return "green";
  if (score < 6.5) return "yellow";
  return "red";
}

/**
 * Share-your-result card for virality. Generates a branded PNG from a hidden
 * DOM node and either uses the native share sheet (with file attachment) on
 * mobile, or falls back to download + text-share on desktop.
 *
 * Free for all users — every share is a pre-launch marketing impression.
 */
export default function ShareResultCard({ analysis }) {
  const cardRef = useRef(null);
  const [busy, setBusy] = useState(false);

  if (!analysis || typeof analysis.risk_score !== "number") return null;

  const score = analysis.risk_score;
  const level = analysis.risk_level || levelFromScore(score);
  const cfg = LEVEL[level] || LEVEL.yellow;
  // Truncate the original text for the image (not the full message)
  const snippet = (analysis.original_text || "")
    .trim()
    .slice(0, 140);
  const snippetSuffix = (analysis.original_text || "").length > 140 ? "…" : "";

  const capture = async () => {
    if (!cardRef.current) return null;
    // Briefly make it visible so html-to-image captures styles accurately
    const el = cardRef.current;
    el.style.position = "fixed";
    el.style.left = "0";
    el.style.top = "0";
    el.style.zIndex = "-1";
    el.style.opacity = "1";
    try {
      const dataUrl = await toPng(el, {
        pixelRatio: 2,
        cacheBust: true,
        backgroundColor: "#F7F9FB",
      });
      return dataUrl;
    } finally {
      el.style.position = "absolute";
      el.style.left = "-99999px";
      el.style.top = "-99999px";
      el.style.opacity = "0";
    }
  };

  const dataUrlToFile = async (dataUrl) => {
    const res = await fetch(dataUrl);
    const blob = await res.blob();
    return new File([blob], "sayitbetter-result.png", { type: "image/png" });
  };

  const handleShareImage = async () => {
    setBusy(true);
    Events.trackEvent?.("share_result_click", { risk_level: level, score });
    try {
      const dataUrl = await capture();
      if (!dataUrl) throw new Error("Capture failed");
      const file = await dataUrlToFile(dataUrl);

      // Try native share with image attached (iOS + modern Android)
      if (
        navigator.share &&
        navigator.canShare &&
        navigator.canShare({ files: [file] })
      ) {
        await navigator.share({
          files: [file],
          title: "SayItBetter",
          text: SHARE_CAPTION,
        });
        Events.shareApp("result_image_native:post_analysis");
        toast.success("Shared!");
        return;
      }

      // Fallback: download PNG + copy caption
      const a = document.createElement("a");
      a.href = dataUrl;
      a.download = "sayitbetter-result.png";
      document.body.appendChild(a);
      a.click();
      a.remove();
      try {
        await navigator.clipboard.writeText(SHARE_CAPTION);
      } catch (_) { /* noop */ }
      Events.shareApp("result_image_download:post_analysis");
      toast.success("Image saved — caption copied too. Post it anywhere!");
    } catch (e) {
      Events.shareApp("result_image_error:post_analysis");
      toast.error("Couldn't build the image. Try the Text / WhatsApp share instead.");
    } finally {
      setBusy(false);
    }
  };

  const handleTextShare = () => {
    const textBody = `I just analyzed a message with SayItBetter and it flagged ${score.toFixed(1)}/10 (${cfg.label}). The AI rewrote it in a calmer way — try it free: ${SHARE_URL}`;
    const encoded = encodeURIComponent(textBody);
    Events.shareApp("result_sms:post_analysis");
    try {
      window.location.href = `sms:?&body=${encoded}`;
    } catch (_) {
      toast.info("Open Messages and paste: " + SHARE_URL, { duration: 6000 });
    }
  };

  return (
    <>
      {/* Hidden render surface for the shareable card (1080x1080 square — Instagram/TikTok friendly) */}
      <div
        ref={cardRef}
        aria-hidden="true"
        style={{ position: "absolute", left: "-99999px", top: "-99999px", opacity: 0, pointerEvents: "none" }}
      >
        <ShareableResultImage
          level={level}
          levelLabel={cfg.label}
          levelColor={cfg.color}
          score={score}
          snippet={snippet + snippetSuffix}
        />
      </div>

      {/* The visible share card */}
      <div
        className="rounded-2xl p-4 shadow-soft border border-[#E5E7EB] bg-white"
        data-testid="share-result-card"
      >
        <div className="flex items-start gap-3 mb-3">
          <div
            className="w-11 h-11 rounded-2xl flex items-center justify-center flex-shrink-0 shadow-sm"
            style={{
              background: "linear-gradient(135deg, #4FB3A5 0%, #2F5BFF 100%)",
            }}
          >
            <Share2 size={18} className="text-white" />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="text-[15px] font-bold text-[#1F3A5F] leading-tight mb-0.5">
              Share your result
            </h3>
            <p className="text-[12px] text-[#6B7280] leading-snug mb-1.5">
              Post the before-it-goes-wrong moment. Great for TikTok, Instagram, and group chats.
            </p>
            <p className="text-[11px] text-[#B4762D] font-semibold flex items-center gap-1">
              <Flame size={11} />
              Your shared post tells friends about the $5.99 pre-launch deal
            </p>
          </div>
        </div>

        <div className="flex gap-2">
          <button
            onClick={handleShareImage}
            disabled={busy}
            data-testid="share-result-image-btn"
            className="flex-1 inline-flex items-center justify-center gap-1.5 bg-gradient-to-r from-[#1F3A5F] to-[#2F5BFF] hover:brightness-110 text-white font-semibold text-[13px] py-2.5 rounded-xl active:scale-95 transition-all shadow-sm disabled:opacity-60"
          >
            {busy ? (
              <><Loader2 size={14} className="animate-spin" /> Creating image…</>
            ) : (
              <><Download size={14} /> Share image</>
            )}
          </button>
          <button
            onClick={handleTextShare}
            disabled={busy}
            data-testid="share-result-text-btn"
            className="inline-flex items-center justify-center gap-1.5 bg-[#EAF6F4] hover:bg-[#d4ebe7] text-[#1F3A5F] font-semibold text-[13px] px-4 py-2.5 rounded-xl active:scale-95 transition-all"
          >
            <MessageSquare size={14} />
            Text
          </button>
        </div>
      </div>
    </>
  );
}

/**
 * The offscreen DOM that becomes the shared PNG. 1080×1080 for social feeds.
 * Using inline styles so html-to-image captures everything without CSS dependencies.
 */
function ShareableResultImage({ level, levelLabel, levelColor, score, snippet }) {
  return (
    <div
      style={{
        width: 1080,
        height: 1080,
        boxSizing: "border-box",
        padding: 72,
        fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif",
        background: "linear-gradient(135deg, #1F3A5F 0%, #2a4a75 50%, #4FB3A5 100%)",
        color: "#fff",
        position: "relative",
        display: "flex",
        flexDirection: "column",
        justifyContent: "space-between",
      }}
    >
      {/* Logo + brand */}
      <div style={{ display: "flex", alignItems: "center", gap: 18 }}>
        <div
          style={{
            width: 78, height: 78, borderRadius: 22,
            background: "#fff", display: "flex", alignItems: "center", justifyContent: "center",
            boxShadow: "0 10px 30px rgba(0,0,0,0.2)",
          }}
        >
          <svg viewBox="0 0 64 64" width="54" height="54" xmlns="http://www.w3.org/2000/svg">
            <path d="M32 6C16 6 6 17 6 28C6 34 9 40 15 44V54L27 47C29 47 30 48 32 48C48 48 58 37 58 28C58 17 48 6 32 6Z" fill="#2F5BFF" />
            <path d="M32 38L28 34C20 27 16 23 16 18C16 14 19 11 24 11C27 11 29 12 32 15C35 12 37 11 40 11C45 11 48 14 48 18C48 23 44 27 36 34L32 38Z" fill="#fff" />
          </svg>
        </div>
        <div>
          <div style={{ fontSize: 40, fontWeight: 800, letterSpacing: -0.5 }}>SayItBetter</div>
          <div style={{ fontSize: 20, opacity: 0.8, marginTop: 2 }}>Think before you send.</div>
        </div>
      </div>

      {/* The result */}
      <div style={{ display: "flex", flexDirection: "column", gap: 36 }}>
        <div
          style={{
            background: "rgba(255,255,255,0.12)",
            border: "1px solid rgba(255,255,255,0.2)",
            borderRadius: 28,
            padding: "32px 36px",
            backdropFilter: "blur(8px)",
          }}
        >
          <div style={{ fontSize: 18, fontWeight: 700, textTransform: "uppercase", letterSpacing: 2, opacity: 0.7, marginBottom: 14 }}>
            I was about to send this
          </div>
          <div
            style={{
              fontSize: 34,
              lineHeight: 1.35,
              fontWeight: 500,
              fontStyle: "italic",
              color: "rgba(255,255,255,0.95)",
            }}
          >
            "{snippet}"
          </div>
        </div>

        {/* Risk meter */}
        <div style={{ display: "flex", alignItems: "center", gap: 32 }}>
          <div
            style={{
              width: 200, height: 200, borderRadius: 999,
              background: "#fff",
              display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
              boxShadow: "0 10px 30px rgba(0,0,0,0.25)",
              border: `10px solid ${levelColor}`,
            }}
          >
            <div style={{ fontSize: 84, fontWeight: 900, color: "#1F3A5F", lineHeight: 1 }}>
              {score.toFixed(1)}
            </div>
            <div style={{ fontSize: 20, fontWeight: 700, color: "#6B7280", marginTop: 4 }}>/ 10</div>
          </div>
          <div style={{ flex: 1 }}>
            <div
              style={{
                display: "inline-block",
                background: "#fff",
                color: levelColor,
                fontSize: 28,
                fontWeight: 800,
                padding: "8px 20px",
                borderRadius: 999,
                marginBottom: 16,
              }}
            >
              {levelLabel.toUpperCase()} RISK
            </div>
            <div style={{ fontSize: 30, fontWeight: 700, lineHeight: 1.3 }}>
              {level === "red"
                ? "SayItBetter saved me from a fight."
                : level === "yellow"
                ? "SayItBetter helped me calm it down."
                : "SayItBetter gave me a cleaner way to say it."}
            </div>
          </div>
        </div>
      </div>

      {/* CTA */}
      <div
        style={{
          background: "rgba(255,255,255,0.15)",
          border: "2px solid rgba(255,255,255,0.3)",
          borderRadius: 20,
          padding: "20px 32px",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <div>
          <div style={{ fontSize: 24, fontWeight: 700 }}>Try it free — 3 checks/day</div>
          <div style={{ fontSize: 20, opacity: 0.85, marginTop: 4 }}>
            Reserve 1 year of PRO for just $5.99 (pre-launch)
          </div>
        </div>
        <div
          style={{
            fontSize: 26,
            fontWeight: 800,
            color: "#1F3A5F",
            background: "#fff",
            borderRadius: 16,
            padding: "16px 28px",
            whiteSpace: "nowrap",
          }}
        >
          sayitbetter.me
        </div>
      </div>
    </div>
  );
}
